/**
 * ground_rules — WHICH ground layer lies WHERE, as explicit rules (ground
 * material v2, 2026-09-25; research §7 R1 / R7 / R10 / R11).
 *
 * Real landscapes change surface every 10–200 m by topographic position: rock
 * where it is steep, scree at the feet of the steep, mud and moss where water
 * gathers, dry grass on crests, litter under canopy, snow in the lee hollows.
 * These rules read a chunk's shape — slope, a FINE curvature (8 m grid) and a
 * COARSE one (64 m grid), a TWI-like wetness (concavity × low slope) and the
 * horizontal distance to water (sea, river, lake) — plus the six climate
 * weights, altitude, forest density, roads, fields and a 40 m noise, and write
 * 12 layer weights + a tint + masks per vertex (lib/ground_layers.ts layout).
 *
 * LOD-INDEPENDENT BY CONSTRUCTION: the fields are sampled on FIXED world grids
 * (8 m / 64 m / 32 m, anchored to world multiples) and bilinearly read at each
 * render vertex, so a chunk rebuilt at another seg shows the same ground — the
 * curvature of a seg-10 chunk's own 25.6 m cells would change the rules at
 * every re-LOD.
 *
 * HEIGHTS NEVER CHANGE: everything here is render-only (the analytic terrain,
 * its three mirrors and the kernel parity gate are untouched). Pure: no three,
 * no DOM — the chunk worker, the far worker and `verify:ground-rules` run it.
 */
import { BIOMES, type Biome } from '../../../shared/terrain.js';
import { GROUND_STRIDE, OFF_TINT, OFF_MASK, ROUTE_NONE, BIOME_TINT, GROUND_LAYER_COUNT, LAYER_INDEX } from './ground_layers.js';
import { turfColorAt } from './grass/turf_core.js'; // turf ↔ grass (vegetation agent, R2 2026-09-25): the turf's colour is the grass layer's rule

// ── grids ──────────────────────────────────────────────────────────────────
export const FINE_STEP = 8;
export const FINE_MARGIN = 2;
export const COARSE_STEP = 64;
export const COARSE_MARGIN = 2;
export const FOREST_STEP = 32;
export const FOREST_MARGIN = 1;

export interface GridSpec { x0: number; z0: number; n: number; step: number }
/** A world-anchored grid covering [minX, minX+size] (+ margin nodes each side). */
export function gridFor(minX: number, minZ: number, size: number, step: number, margin: number): GridSpec {
  const x0 = Math.floor(minX / step) * step - margin * step;
  const z0 = Math.floor(minZ / step) * step - margin * step;
  const x1 = Math.ceil((minX + size) / step) * step + margin * step;
  return { x0, z0, n: Math.round((x1 - x0) / step) + 1, step };
}
/** Bilinear read of a node field (row = z). Clamped to the grid. */
export function bilinear(g: GridSpec, f: ArrayLike<number>, x: number, z: number): number {
  let fx = (x - g.x0) / g.step, fz = (z - g.z0) / g.step;
  const m = g.n - 1;
  if (fx < 0) fx = 0; else if (fx > m) fx = m;
  if (fz < 0) fz = 0; else if (fz > m) fz = m;
  let ix = Math.floor(fx), iz = Math.floor(fz);
  if (ix >= m) ix = m - 1; if (iz >= m) iz = m - 1;
  const tx = fx - ix, tz = fz - iz;
  const i = iz * g.n + ix;
  const a = f[i], b = f[i + 1], c = f[i + g.n], d = f[i + g.n + 1];
  return (a * (1 - tx) + b * tx) * (1 - tz) + (c * (1 - tx) + d * tx) * tz;
}

// ── water ──────────────────────────────────────────────────────────────────
export const WATER_NONE = 0, WATER_SEA = 1, WATER_RIVER = 2, WATER_LAKE = 3;
/** Distance (m) past which the water-distance field stops counting. */
export const WATER_FAR_M = 40;

/**
 * Horizontal distance (m) from every node to the nearest WATER node (0 on
 * water) — a two-pass 3-4 chamfer, ≤ 8 % over Euclidean. `kind` carries the
 * nearest water's kind forward with the distance.
 */
export function waterDistance(g: GridSpec, water: Uint8Array): { d: Float32Array; kind: Uint8Array } {
  const n = g.n, N = n * n, INF = 1e9;
  const d = new Float32Array(N), k = new Uint8Array(N);
  for (let i = 0; i < N; i++) { d[i] = water[i] ? 0 : INF; k[i] = water[i]; }
  const a = g.step, b = g.step * Math.SQRT2;
  const relax = (i: number, j: number, w: number): void => { if (d[j] + w < d[i]) { d[i] = d[j] + w; k[i] = k[j]; } };
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
    const i = r * n + c;
    if (c > 0) relax(i, i - 1, a);
    if (r > 0) { relax(i, i - n, a); if (c > 0) relax(i, i - n - 1, b); if (c < n - 1) relax(i, i - n + 1, b); }
  }
  for (let r = n - 1; r >= 0; r--) for (let c = n - 1; c >= 0; c--) {
    const i = r * n + c;
    if (c < n - 1) relax(i, i + 1, a);
    if (r < n - 1) { relax(i, i + n, a); if (c < n - 1) relax(i, i + n + 1, b); if (c > 0) relax(i, i + n - 1, b); }
  }
  // Past the reach nothing counts as "near water" — the kind goes with the distance.
  for (let i = 0; i < N; i++) if (d[i] >= WATER_FAR_M) { d[i] = WATER_FAR_M; k[i] = 0; }
  return { d, kind: k };
}

// ── the chunk rule field ───────────────────────────────────────────────────
export interface RuleField {
  fine: GridSpec; coarse: GridSpec; forest: GridSpec;
  /** Fine-grid node fields. `slopeMax` = the steeper of the one-sided 8 m differences per axis (the steep rule's). */
  slope: Float32Array; slopeMax: Float32Array; kf: Float32Array; shade: Float32Array; dWater: Float32Array; waterKind: Uint8Array; bend: Float32Array;
  /** Coarse-grid node curvature. */
  kc: Float32Array;
  /** Forest density on the forest grid. */
  fd: Float32Array;
}

export interface RuleFieldInput {
  minX: number; minZ: number; size: number;
  /** Heights on `fine` / `coarse` (row = z) — the analytic landform terrain, no stamps. */
  fine: GridSpec; fineH: ArrayLike<number>;
  coarse: GridSpec; coarseH: ArrayLike<number>;
  forest: GridSpec; forestD: ArrayLike<number>;
  /** Water class of a fine node (sea / river channel / lake), from its height and the hydrology. */
  waterAt: (x: number, z: number, h: number) => number;
  /** Inner-bend strength at a fine node near a river (0..1 on the inside of a bend, 0 elsewhere). */
  bendAt?: (x: number, z: number) => number;
}

/** Laplacian (1/m) and gradient of a node field at interior node i (edges copy their neighbour). */
function derive(g: GridSpec, h: ArrayLike<number>, lap: Float32Array, slope?: Float32Array, shade?: Float32Array, slopeMax?: Float32Array): void {
  const n = g.n, s = g.step;
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
    const i = r * n + c;
    const cl = c > 0 ? c - 1 : c, cr = c < n - 1 ? c + 1 : c, ru = r > 0 ? r - 1 : r, rd = r < n - 1 ? r + 1 : r;
    const hl = h[r * n + cl], hr = h[r * n + cr], hu = h[ru * n + c], hd = h[rd * n + c], hc = h[i];
    const dx = (hr - hl) / ((cr - cl) * s || s), dz = (hd - hu) / ((rd - ru) * s || s);
    // Positive = concave (a hollow, a valley floor); negative = convex (a crest, a knoll).
    lap[i] = (hl + hr + hu + hd - 4 * hc) / (s * s);
    if (slope) slope[i] = Math.sqrt(dx * dx + dz * dz);
    // STEEP-FACE LAW (ground, 2026-09-26): the central difference spans 16 m (two cells), which halves a terrace
    // riser 5–20 m wide — mesh slopes > 0.8 read 0.63–0.70 and the steep rule fired 13–35 % on them (the
    // session-2 "rock 0 on steep" fail, measured on four real risers). Per axis, the steeper of the two ONE-SIDED
    // 8 m differences (the max-drop slope) reads them at 0.92–1.07 and stays ≈ central on smooth ground.
    if (slopeMax) {
      const fx = c < n - 1 ? (hr - hc) / s : 0, bx = c > 0 ? (hc - hl) / s : 0;
      const fz = r < n - 1 ? (hd - hc) / s : 0, bz = r > 0 ? (hc - hu) / s : 0;
      const mx = Math.abs(fx) > Math.abs(bx) ? fx : bx, mz = Math.abs(fz) > Math.abs(bz) ? fz : bz;
      slopeMax[i] = Math.sqrt(mx * mx + mz * mz);
    }
    // North (−Z) facing: the ground rises toward +Z, so the face looks away from a
    // sun that crosses the southern sky — the shaded aspect moss prefers.
    if (shade) shade[i] = Math.max(0, Math.min(1, dz * 2.2));
  }
}

export function buildRuleField(inp: RuleFieldInput): RuleField {
  const { fine, coarse, forest } = inp;
  const N = fine.n * fine.n;
  const slope = new Float32Array(N), slopeMax = new Float32Array(N), kf = new Float32Array(N), shade = new Float32Array(N), bend = new Float32Array(N);
  derive(fine, inp.fineH, kf, slope, shade, slopeMax);
  const kc = new Float32Array(coarse.n * coarse.n);
  derive(coarse, inp.coarseH, kc);
  const water = new Uint8Array(N);
  for (let r = 0; r < fine.n; r++) for (let c = 0; c < fine.n; c++) {
    const i = r * fine.n + c;
    water[i] = inp.waterAt(fine.x0 + c * fine.step, fine.z0 + r * fine.step, inp.fineH[i]);
  }
  const { d: dWater, kind: waterKind } = waterDistance(fine, water);
  if (inp.bendAt) for (let r = 0; r < fine.n; r++) for (let c = 0; c < fine.n; c++) {
    const i = r * fine.n + c;
    if (waterKind[i] === WATER_RIVER && dWater[i] < 24) bend[i] = inp.bendAt(fine.x0 + c * fine.step, fine.z0 + r * fine.step);
  }
  return { fine, coarse, forest, slope, slopeMax, kf, shade, dWater, waterKind, bend, kc, fd: Float32Array.from(inp.forestD) };
}

// ── the rule ───────────────────────────────────────────────────────────────
export interface GroundRuleInput {
  h: number; sea: number;
  /** Six climate weights in BIOMES order (sum ≈ 1). */
  biome: ArrayLike<number>;
  slope: number; kf: number; kc: number; shade: number;
  /** The steep rule's slope (the field's max-drop reading, RuleField.slopeMax); absent → `slope`. */
  slopeMax?: number;
  dWater: number; waterKind: number; bend: number;
  fd: number; road: number; field: number;
  /** Signed across-track distance (m) to the nearest regional route's centreline; ROUTE_NONE when none within reach. */
  routeD: number;
  /** Terrace (strata band) weight 0..1. */
  terrace: number;
  /** Deterministic noise −1..1 at ~40 m. */
  n40: number;
}

const ss = (a: number, b: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const cl = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
/** Weights under this are dropped (the shader skips a layer under 0.02 anyway). */
export const WEIGHT_FLOOR = 0.015;
/** At most this many layers per vertex (the fragment pays for the union over a triangle). */
export const MAX_LAYERS_PER_VERTEX = 4;

const wScratch = new Float64Array(GROUND_LAYER_COUNT);
const keep = new Uint8Array(GROUND_LAYER_COUNT);

/**
 * Evaluate the rules for one vertex and write GROUND_STRIDE floats at `o`:
 * 12 normalised weights (≤ MAX_LAYERS_PER_VERTEX non-zero), tint rgb +
 * dryness, masks (road, field, wet, route-across).
 */
export function groundRule(inp: GroundRuleInput, out: Float32Array, o: number): void {
  const b = inp.biome;
  const reg = b[0], cry = b[1], dun = b[2], roc = b[3], mos = b[4], fun = b[5];
  const s = inp.slope, h = inp.h, alt = h - inp.sea;
  const n01 = inp.n40 * 0.5 + 0.5;
  const flat = 1 - ss(0.12, 0.5, s);
  const concF = ss(0.004, 0.03, inp.kf), convF = ss(0.004, 0.03, -inp.kf);
  const concC = ss(0.0004, 0.004, inp.kc), convC = ss(0.0004, 0.004, -inp.kc);
  // ── WATER EDGES (R10): 1 at the water's edge → 0 at 12 m, only where a
  //    sea / river / lake is in reach of the fine grid.
  const wk = inp.waterKind;
  const wetEdge = wk ? 1 - ss(2, 12, inp.dWater) : 0;
  // TWI-like wetness (Beven & Kirkby's ln(a / tan β) stand-in): water gathers in
  // concave ground and stays where the slope cannot shed it.
  const twi = cl(concF * 0.55 + concC * 0.6) * (1 - ss(0.06, 0.3, s));
  const wet = cl(Math.max(wetEdge, twi * 0.75 + mos * 0.35 * flat));

  // ── SNOW: caps above a wandering snowline, shed by steep faces; drifts fill
  //    concave lee cells just under the line (R7).
  const snowLine = 320 + inp.n40 * 18;
  const snowCap = ss(snowLine, snowLine + 60, h) * (1 - ss(0.35, 1.1, s));
  const drift = ss(snowLine - 55, snowLine + 5, h) * concC * 0.8 * (1 - ss(0.2, 0.6, s));
  const snow = cl(snowCap + drift);

  // ── ROCK: steep faces, convex crests on moderate slopes, the alpine band,
  //    and a rocky coast where the coastal slope is steep (R10).
  const coastBand = 1 - ss(inp.sea + 1.5, inp.sea + 6, h);
  const steep = ss(0.55, 0.95, inp.slopeMax ?? s); // the STEEP-FACE LAW (derive): risers read at their real slope
  const crest = convC * ss(0.22, 0.5, s) * 0.6;
  const alpine = ss(240, 320, h + inp.n40 * 14) * (0.55 + 0.45 * ss(0.12, 0.35, s));
  const rockyCoast = coastBand * ss(0.35, 0.5, s);
  const rock = cl(steep + crest + alpine * (1 - snow) + rockyCoast);
  // Strata where the terrace bands run, in the desert (sandstone) and the highlands.
  const strataSel = cl(inp.terrace * 1.6 + dun * 0.6 + roc * 0.35 + n01 * 0.25 - 0.1);

  // ── GRAVEL: talus at the concave feet below steep ground, drainage lines,
  //    bars on a river's inner bends (R10), route verges (R11).
  const talus = ss(0.22, 0.55, s) * concC * (1 - steep) * 0.9;
  const drainage = concF * ss(0.04, 0.25, s) * 0.55 * (1 - wetEdge * 0.5);
  const bar = wk === WATER_RIVER ? cl(inp.bend) * (1 - ss(1, 9, inp.dWater)) * flat : 0;
  const verge = inp.road > 0.06 && inp.road < 0.9 ? 4 * inp.road * (1 - inp.road) * 0.6 : 0;
  const gravel = cl(talus + drainage + bar * 1.2 + verge * (1 - inp.field));

  // ── SAND: the beach band without ripples (never on a steep coast), dunes in the desert.
  const beach = coastBand * (1 - ss(0.3, 0.45, s)) * (wk === WATER_SEA || alt < 6 ? 1 : 0);
  const dune = dun * flat * (1 - beach) * (0.7 + 0.3 * n01);

  // ── MUD: the wet bank 2–12 m from rivers and lakes (the sea's edge is sand),
  //    wet hollows in the wetland.
  const bank = wetEdge * (1 - ss(0.15, 0.4, s)) * (wk === WATER_RIVER || wk === WATER_LAKE ? 1 : 0.25);
  const mud = cl(bank * (1 - bar) + twi * mos * 0.4 + concF * wet * 0.25) * (1 - beach * 0.8);

  // ── MOSS: the wetland, shaded (north-facing) hollows, humid forest floor.
  const moss = cl(mos * 0.5 * (0.4 + 0.6 * wet) + inp.shade * concF * flat * 0.5 + inp.fd * twi * 0.35 + wet * 0.15 * (cry + mos)) * (1 - snow);

  // ── LITTER under canopy; ALIEN grit in the crystal / fungal climates.
  const litter = ss(0.25, 0.65, inp.fd) * (1 - steep) * 0.95;
  const alien = (cry * 0.55 + fun * 0.65) * (1 - steep) * (0.6 + 0.4 * flat);

  // ── TURF: meadow patches over the temperate climates, gentle ground, below
  //    the alpine band, thinned under canopy.
  const meadow = cl(n01 * 1.4 - 0.25);
  const turf = (reg * 0.6 + cry * 0.25 + mos * 0.3 + roc * 0.25) * meadow * flat * (1 - ss(190, 260, h)) * (1 - inp.fd * 0.8) * (1 - wetEdge * 0.7);

  const w = wScratch;
  w[LAYER_INDEX.rock] = rock * (1 - strataSel);
  w[LAYER_INDEX.strata] = rock * strataSel;
  w[LAYER_INDEX.snow] = snow * 1.4;
  w[LAYER_INDEX.gravel] = gravel * (1 - snow);
  w[LAYER_INDEX.beach] = beach * (1 - rock);
  w[LAYER_INDEX.dune] = dune * (1 - rock) * (1 - snow);
  w[LAYER_INDEX.mud] = mud * (1 - rock);
  w[LAYER_INDEX.moss] = moss * (1 - rock * 0.7);
  w[LAYER_INDEX.litter] = litter * (1 - snow) * (1 - wetEdge * 0.6);
  w[LAYER_INDEX.alien] = alien * (1 - snow) * (1 - rock);
  w[LAYER_INDEX.turf] = turf * (1 - inp.field) * (1 - inp.road);
  // LOAM: the base — what the rest leaves, never nothing; fields and roads are loam
  // (tilled / compacted — the shader reads the field and road masks for their look).
  let others = 0;
  for (let i = 0; i < GROUND_LAYER_COUNT; i++) if (i !== LAYER_INDEX.loam) others += w[i];
  w[LAYER_INDEX.loam] = Math.max(0.08, 1.05 - others) * (1 - snow * 0.8) + inp.field * 1.5 + inp.road * 1.2;

  // Keep the strongest MAX_LAYERS_PER_VERTEX, drop the faint, renormalise
  // (a selection, not a sort — this runs per vertex, 2,401 times a near chunk).
  keep.fill(0);
  for (let k = 0; k < MAX_LAYERS_PER_VERTEX; k++) {
    let bi = -1, bv = WEIGHT_FLOOR;
    for (let i = 0; i < GROUND_LAYER_COUNT; i++) if (!keep[i] && w[i] >= bv) { bv = w[i]; bi = i; }
    if (bi < 0) break;
    keep[bi] = 1;
  }
  let sum = 0;
  for (let i = 0; i < GROUND_LAYER_COUNT; i++) { if (!keep[i]) w[i] = 0; else sum += w[i]; }
  if (!(sum > 0)) { for (let i = 0; i < GROUND_LAYER_COUNT; i++) w[i] = 0; w[LAYER_INDEX.loam] = 1; sum = 1; }
  for (let i = 0; i < GROUND_LAYER_COUNT; i++) out[o + i] = w[i] / sum;

  // ── TINT: the climate tint blended by the biome weights; DRYNESS (tint.a) —
  //    crests and the desert dry, wet ground lush (R7: "dry grass on crests").
  let tr = 0, tg = 0, tb = 0;
  for (let i = 0; i < BIOMES.length; i++) { const t = BIOME_TINT[BIOMES[i] as Biome]; const wi = b[i] ?? 0; tr += t[0] * wi; tg += t[1] * wi; tb += t[2] * wi; }
  const bs = reg + cry + dun + roc + mos + fun || 1;
  out[o + OFF_TINT] = tr / bs; out[o + OFF_TINT + 1] = tg / bs; out[o + OFF_TINT + 2] = tb / bs;
  out[o + OFF_TINT + 3] = cl(convC * 0.8 + convF * 0.3 + dun * 0.6 + (1 - wet) * 0.2 - wet * 0.6 + ss(160, 240, h) * 0.25);
  // ── MASKS: road, field, wet (the shader darkens + glosses, puddles, R10), route-across (R11).
  out[o + OFF_MASK] = cl(inp.road);
  out[o + OFF_MASK + 1] = cl(inp.field) * (1 - cl(inp.road));
  out[o + OFF_MASK + 2] = wet;
  out[o + OFF_MASK + 3] = Number.isFinite(inp.routeD) ? Math.max(-ROUTE_NONE, Math.min(ROUTE_NONE, inp.routeD)) : ROUTE_NONE;
}

/** Deterministic value noise (−1..1) on a world lattice — the rules' 40 m variation (sign-safe, no three hash). */
export function ruleNoise(x: number, z: number, wl: number, salt = 0): number {
  const gx = Math.floor(x / wl), gz = Math.floor(z / wl);
  const fx = x / wl - gx, fz = z / wl - gz;
  const sx = fx * fx * (3 - 2 * fx), sz = fz * fz * (3 - 2 * fz);
  const hsh = (i: number, j: number): number => {
    let hh = (Math.imul(i, 374761393) ^ Math.imul(j, 668265263) ^ Math.imul(salt + 1, 1274126177)) | 0;
    hh = Math.imul(hh ^ (hh >>> 13), 1274126177); hh ^= hh >>> 16;
    return (hh >>> 0) / 4294967296;
  };
  const a = hsh(gx, gz), bb = hsh(gx + 1, gz), c = hsh(gx, gz + 1), d = hsh(gx + 1, gz + 1);
  return ((a * (1 - sx) + bb * sx) * (1 - sz) + (c * (1 - sx) + d * sx) * sz) * 2 - 1;
}

/** One vertex's rule inputs read off a built field (the chunk worker's inner loop). */
export function sampleRuleField(f: RuleField, x: number, z: number, into: GroundRuleInput): GroundRuleInput {
  into.slope = bilinear(f.fine, f.slope, x, z);
  into.slopeMax = bilinear(f.fine, f.slopeMax, x, z);
  into.kf = bilinear(f.fine, f.kf, x, z);
  into.shade = bilinear(f.fine, f.shade, x, z);
  into.dWater = bilinear(f.fine, f.dWater, x, z);
  into.bend = bilinear(f.fine, f.bend, x, z);
  into.kc = bilinear(f.coarse, f.kc, x, z);
  into.fd = bilinear(f.forest, f.fd, x, z);
  // The water KIND is categorical: take the nearest node's.
  const c = Math.round((x - f.fine.x0) / f.fine.step), r = Math.round((z - f.fine.z0) / f.fine.step);
  const i = Math.max(0, Math.min(f.fine.n - 1, r)) * f.fine.n + Math.max(0, Math.min(f.fine.n - 1, c));
  into.waterKind = f.waterKind[i];
  return into;
}

export function emptyRuleInput(): GroundRuleInput {
  return { h: 0, sea: 0, biome: [1, 0, 0, 0, 0, 0], slope: 0, kf: 0, kc: 0, shade: 0, dWater: WATER_FAR_M, waterKind: 0, bend: 0, fd: 0, road: 0, field: 0, routeD: ROUTE_NONE, terrace: 0, n40: 0 };
}

const turfScratch = new Float64Array(3);
/** The per-vertex colour the minimal tier and the far tier's macro bake use: Σ w·mean·tint (linear); the turf layer's
 *  term is the grass layer's turf rule (lib/grass/turf_core.ts). */
export function ruleColour(g: ArrayLike<number>, o: number, mean: readonly (readonly [number, number, number])[], tintK: readonly number[]): [number, number, number] {
  let r = 0, gg = 0, b = 0;
  const tr = g[o + OFF_TINT], tg = g[o + OFF_TINT + 1], tb = g[o + OFF_TINT + 2];
  for (let i = 0; i < GROUND_LAYER_COUNT; i++) {
    const w = g[o + i]; if (!w) continue;
    if (i === LAYER_INDEX.turf) {
      // turf ↔ grass (vegetation agent, R2): the turf's mean is the grass layer's turf rule over this vertex's tint,
      // dryness (tint.a) and wetness (mask.z) — what the near shader shades the turf layer to, so the far bake agrees.
      turfColorAt(tr, tg, tb, g[o + OFF_TINT + 3], g[o + OFF_MASK + 2], turfScratch);
      r += w * turfScratch[0]; gg += w * turfScratch[1]; b += w * turfScratch[2];
      continue;
    }
    const k = tintK[i];
    r += w * mean[i][0] * (1 + (tr - 1) * k); gg += w * mean[i][1] * (1 + (tg - 1) * k); b += w * mean[i][2] * (1 + (tb - 1) * k);
  }
  return [r, gg, b];
}

export { GROUND_STRIDE };
